import { useMemo, useState, type FormEvent, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Ban,
  Gauge,
  Loader2,
  Lock,
  Mail,
  MailCheck,
  Route as RouteIcon,
  Save,
  ShieldCheck,
} from "lucide-react";

import { LiveSwitchCard, type LiveEnvelope } from "@/components/admin/marketing/live-switch-card";
import {
  marketingSettingsQueryKey,
  marketingSettingsQueryOptions,
  type MarketingSettingsEnvelope,
} from "@/components/admin/marketing/readiness/live-queries";
import { ActivationMatrix } from "@/components/admin/marketing/settings/activation-matrix";
import { ChannelProvidersCard } from "@/components/admin/marketing/settings/channel-config-card";
import {
  COUNTRY_DEFAULTS,
  DEFAULT_MARKETING_SETTINGS,
  MARKETING_CHANNELS,
  type MarketingChannelKey,
  type MarketingSettings,
  type MarketingSettingsForm,
} from "@/components/admin/marketing/settings/types";
import { SuppressionList } from "@/components/admin/marketing/suppression/suppression-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminContext } from "@/lib/admin-context";
import { withSavingToast } from "@/lib/ui/toast";

/**
 * Admin → Marketing → Settings.
 *
 * Sections:
 *  1. Live switch — env ceiling (pontimed-workers) + audited admin switch
 *  2. Sending providers — read-only: SendGrid for email, BulkGate for SMS,
 *     Viber and WhatsApp, with the configured state the workers report
 *  3. Per-market activation (countries × channels) — ENFORCED on every send
 *  4. Sending rules per country — sender identity, weekly caps, double opt-in,
 *     blacklists, active journeys — ENFORCED on every send
 *  5. Unsubscribe + sender identity — automatic, read-only
 *  6. Suppression list
 *
 * Endpoints (apps/api/src/routes/admin-marketing.ts):
 *  - GET  /api/admin/marketing/settings   → { settings, live, workerEnv,
 *                                             workerReportAgeMinutes, effectiveLive }
 *                                           (the legacy bare array is still tolerated)
 *  - POST /api/admin/marketing/settings   upsert one country row
 *  - PUT  /api/admin/marketing/live       { enabled, confirm }
 *  - GET  /api/admin/marketing/journeys   (journey codes for the per-country filter)
 *  - /suppressions/*                      (see suppression-list.tsx)
 *
 * Only what a send actually reads is editable here — the per-country rows
 * (delivery/caps.ts, compose.ts). The earlier provider editor, "send test",
 * provider overrides and quota bars saved nothing and are gone.
 *
 * DB toggle: the settings and journeys queries are keyed on `dbTarget`; the
 * suppression list and the rules form remount per target.
 */

export const Route = createFileRoute("/_authenticated/marketing-settings")({
  component: MarketingSettingsPage,
});

/**
 * Sender addresses must sit on the domain authenticated in SendGrid. MIRRORS
 * AUTHENTICATED_FROM_RE in packages/marketing/src/delivery/compose.ts — any
 * other From is dropped there in favour of the default sender.
 */
const SENDER_DOMAIN = "pontimed.app";
const SENDER_ADDRESS_RE = /^[^\s@<>"]+@pontimed\.app$/i;
/** compose.ts truncates the display name at 64 characters. */
const SENDER_NAME_MAX = 64;

const MAX_EMAILS_PER_WEEK = 50;
const MAX_SMS_PER_WEEK = 20;

/** The MarketingSettings columns — nothing else goes into the upsert body. */
function toFormRow(row: MarketingSettings): MarketingSettingsForm {
  return {
    countryCode: row.countryCode,
    emailEnabled: row.emailEnabled,
    smsEnabled: row.smsEnabled,
    viberEnabled: row.viberEnabled,
    whatsappEnabled: row.whatsappEnabled,
    pushEnabled: row.pushEnabled,
    emailFromName: row.emailFromName,
    emailFromAddress: row.emailFromAddress,
    smsSenderId: row.smsSenderId,
    maxEmailsPerWeek: row.maxEmailsPerWeek,
    maxSmsPerWeek: row.maxSmsPerWeek,
    doubleOptInRequired: row.doubleOptInRequired,
    unsubscribeUrl: row.unsubscribeUrl,
    blacklistDomains: row.blacklistDomains ?? [],
    blacklistPhones: row.blacklistPhones ?? [],
    activeJourneyCodes: row.activeJourneyCodes ?? [],
    defaultLanguage: row.defaultLanguage,
  };
}

/**
 * What the send path enforces for a country with no row — MIRRORS
 * defaultCountrySettings in packages/marketing/src/delivery/caps.ts: email on
 * (3/week), every phone channel off. Creating a row from here must change only
 * what the admin touched, so the "recommended" channels in COUNTRY_DEFAULTS are
 * deliberately NOT switched on (that would start paid SMS/Viber silently).
 */
function defaultRow(countryCode: string): MarketingSettingsForm {
  return {
    ...DEFAULT_MARKETING_SETTINGS,
    countryCode,
    defaultLanguage:
      COUNTRY_DEFAULTS[countryCode]?.lang ?? DEFAULT_MARKETING_SETTINGS.defaultLanguage,
  };
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: unknown } | null;
    return typeof body?.error === "string"
      ? `HTTP ${e.status} — ${body.error}`
      : `HTTP ${e.status}`;
  }
  return e instanceof Error ? e.message : String(e);
}

interface CountryEntry {
  code: string;
  name: string;
  flag: string | null;
}

function MarketingSettingsPage() {
  const { t } = useLingui();
  const { visibleCountries: countries } = useAdminCountries();
  const { dbTarget } = useAdminContext();
  const queryClient = useQueryClient();

  const settingsKey = marketingSettingsQueryKey(dbTarget);
  const settingsQuery = useQuery(marketingSettingsQueryOptions(dbTarget));

  const [actionError, setActionError] = useState<string | null>(null);

  const allSettings = useMemo(() => settingsQuery.data?.settings ?? [], [settingsQuery.data]);

  const settingsByCountry = useMemo(() => {
    const map: Record<string, MarketingSettings | undefined> = {};
    for (const s of allSettings) map[s.countryCode] = s;
    return map;
  }, [allSettings]);

  // The matrix shows what is ENFORCED: a country without a row gets the
  // send path's defaults (email on, phones off), not a row of "off" switches.
  const enforcedByCountry = useMemo(() => {
    const map: Record<string, MarketingSettings | undefined> = { ...settingsByCountry };
    for (const c of countries) {
      if (!map[c.code]) map[c.code] = { id: `default-${c.code}`, ...defaultRow(c.code) };
    }
    return map;
  }, [settingsByCountry, countries]);

  const visibleCountriesForMatrix = useMemo<CountryEntry[]>(
    () =>
      countries.map((c) => {
        const localizedName =
          (typeof c.name === "object" &&
            c.name !== null &&
            (c.name.en ?? Object.values(c.name)[0])) ||
          c.code;
        return {
          code: c.code,
          name: typeof localizedName === "string" ? localizedName : c.code,
          flag: c.flag,
        };
      }),
    [countries],
  );

  const writeRow = (row: MarketingSettings) => {
    queryClient.setQueryData<MarketingSettingsEnvelope>(settingsKey, (prev) => {
      const list = prev?.settings ?? [];
      const idx = list.findIndex((s) => s.countryCode === row.countryCode);
      const next = idx === -1 ? [...list, row] : list.map((s, i) => (i === idx ? row : s));
      return { settings: next, live: prev?.live ?? null };
    });
  };

  /**
   * Upsert one country row. Optimistic, then replaced by the server's row; on
   * failure the toast says so and the cache is refetched. Resolves true on success.
   */
  const saveCountry = async (
    countryCode: string,
    patch: Partial<MarketingSettingsForm>,
    messages: { saving: string; saved: string; error: string },
  ): Promise<boolean> => {
    const existing = settingsByCountry[countryCode];
    const body: MarketingSettingsForm = {
      ...(existing ? toFormRow(existing) : defaultRow(countryCode)),
      ...patch,
      countryCode,
    };
    writeRow({ id: existing?.id ?? `tmp-${countryCode}`, ...body });
    setActionError(null);
    try {
      const saved = await withSavingToast(
        () => apiFetch.post<MarketingSettings>("/api/admin/marketing/settings", body),
        messages,
      );
      writeRow(saved);
      return true;
    } catch (e) {
      setActionError(errorText(e));
      void queryClient.invalidateQueries({ queryKey: settingsKey });
      return false;
    }
  };

  const handleMatrixToggle = (
    countryCode: string,
    channelKey: MarketingChannelKey,
    enabled: boolean,
  ) => {
    const channelLabel = MARKETING_CHANNELS.find((c) => c.key === channelKey)?.label ?? channelKey;
    void saveCountry(
      countryCode,
      { [channelKey]: enabled },
      enabled
        ? {
            saving: t`Turning ${channelLabel} on for ${countryCode}…`,
            saved: t`${channelLabel} is on for ${countryCode}`,
            error: t`Could not change ${channelLabel} for ${countryCode}`,
          }
        : {
            saving: t`Turning ${channelLabel} off for ${countryCode}…`,
            saved: t`${channelLabel} is off for ${countryCode} — those sends are skipped`,
            error: t`Could not change ${channelLabel} for ${countryCode}`,
          },
    );
  };

  const onLiveChanged = (next: LiveEnvelope) => {
    queryClient.setQueryData<MarketingSettingsEnvelope>(settingsKey, (prev) => ({
      settings: prev?.settings ?? [],
      live: next,
    }));
    void queryClient.invalidateQueries({ queryKey: ["admin", "marketing", "readiness"] });
  };

  const loading = settingsQuery.isPending;
  const loadError = settingsQuery.isError ? errorText(settingsQuery.error) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Marketing → Settings</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Delivery switch, sending providers, per-market rules and the suppression list.
          </Trans>
        </p>
      </div>

      {/* 1. Live switch */}
      <LiveSwitchCard
        key={dbTarget}
        kind="marketing"
        endpoint="/api/admin/marketing/live"
        envelope={settingsQuery.data?.live ?? null}
        loading={loading}
        onChanged={onLiveChanged}
        description={
          <Trans>
            Before switching on, run the{" "}
            <Link to="/marketing-readiness" className="text-primary font-medium underline">
              go-live checklist
            </Link>
            .
          </Trans>
        }
      />

      {loadError || actionError ? (
        <div
          className="border-destructive-border bg-destructive-soft text-destructive-strong rounded-lg border p-3 text-sm"
          role="alert"
        >
          {loadError ?? actionError}
        </div>
      ) : null}

      {/* 2. Sending providers (read-only) */}
      <section aria-labelledby="providers-heading" className="space-y-3">
        <h2 id="providers-heading" className="text-lg font-semibold">
          <Trans>Sending providers</Trans>
        </h2>
        <ChannelProvidersCard envelope={settingsQuery.data?.live ?? null} loading={loading} />
      </section>

      {/* 3. Per-market activation */}
      <section aria-labelledby="activation-heading" className="space-y-3">
        <div>
          <h2 id="activation-heading" className="text-lg font-semibold">
            <Trans>Per-market activation</Trans>
          </h2>
          <EnforcedNote>
            <Trans>
              Enforced on every send. A channel switched off here is skipped for every lead in that
              country — the message is recorded as skipped (channel disabled for country), not sent.
            </Trans>
          </EnforcedNote>
        </div>
        {loading ? (
          <Skeleton className="h-72 rounded-lg" />
        ) : (
          <ActivationMatrix
            countries={visibleCountriesForMatrix}
            settingsByCountry={enforcedByCountry}
            onToggle={handleMatrixToggle}
          />
        )}
      </section>

      {/* 4. Sending rules per country */}
      <section aria-labelledby="rules-heading" className="space-y-3">
        <div>
          <h2 id="rules-heading" className="text-lg font-semibold">
            <Trans>Sending rules per country</Trans>
          </h2>
          <EnforcedNote>
            <Trans>
              Enforced on every campaign and journey message: the sender identity, the weekly caps,
              the blacklists and the active journeys below are read at send time, per lead country.
            </Trans>
          </EnforcedNote>
        </div>
        {loading ? (
          <Skeleton className="h-96 rounded-lg" />
        ) : (
          <CountrySendingRules
            countries={visibleCountriesForMatrix}
            settingsByCountry={settingsByCountry}
            onSave={saveCountry}
          />
        )}
      </section>

      {/* 5. Unsubscribe + sender identity (automatic) */}
      <section aria-labelledby="compliance-heading" className="space-y-3">
        <h2 id="compliance-heading" className="text-lg font-semibold">
          <Trans>Unsubscribe and sender identity</Trans>
        </h2>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary-soft text-primary flex size-8 items-center justify-center rounded-md">
                <ShieldCheck className="size-4" aria-hidden />
              </span>
              <Trans>Added automatically — not editable</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-foreground list-disc space-y-1.5 pl-5 text-sm">
              <li>
                <Trans>
                  Every marketing email ends with a footer that cannot be removed: who is sending
                  (the legal entity and its address), why the person receives it, and a personal
                  unsubscribe link.
                </Trans>
              </li>
              <li>
                <Trans>
                  Mail clients also get a one-click unsubscribe header (List-Unsubscribe, RFC 8058),
                  which Gmail and Yahoo require from bulk senders.
                </Trans>
              </li>
              <li>
                <Trans>Every marketing SMS ends with a short opt-out link.</Trans>
              </li>
              <li>
                <Trans>
                  Unsubscribing needs no login and the link never expires. Opening the page does not
                  unsubscribe — the person confirms, or the mail client does it in one click.
                </Trans>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* 6. Suppression list */}
      <section aria-labelledby="suppression-heading" className="space-y-3">
        <div>
          <h2 id="suppression-heading" className="text-lg font-semibold">
            <Trans>Suppression list</Trans>
          </h2>
          <p className="text-muted-foreground text-xs">
            <Trans>
              Addresses that receive no marketing at all — unsubscribes, spam complaints, hard
              bounces, and addresses blocked by an admin.
            </Trans>
          </p>
        </div>
        <SuppressionList key={dbTarget} />
      </section>
    </div>
  );
}

/** "This setting is enforced" — one visual for every rule the send path reads. */
function EnforcedNote({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground mt-1 flex items-start gap-1.5 text-xs">
      <Lock className="text-primary mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Sending rules per country
// ---------------------------------------------------------------------------

interface JourneyOption {
  code: string;
  name: string;
  isActive: boolean;
}

type SaveCountry = (
  countryCode: string,
  patch: Partial<MarketingSettingsForm>,
  messages: { saving: string; saved: string; error: string },
) => Promise<boolean>;

function CountrySendingRules({
  countries,
  settingsByCountry,
  onSave,
}: {
  countries: CountryEntry[];
  settingsByCountry: Record<string, MarketingSettings | undefined>;
  onSave: SaveCountry;
}) {
  const { dbTarget } = useAdminContext();
  const [selected, setSelected] = useState("");
  const countryCode =
    selected && countries.some((c) => c.code === selected) ? selected : (countries[0]?.code ?? "");
  const row = countryCode ? settingsByCountry[countryCode] : undefined;

  const journeysQuery = useQuery({
    queryKey: ["admin", "marketing", "journeys", "options", dbTarget],
    queryFn: async (): Promise<JourneyOption[]> => {
      const raw = await apiFetch.get<unknown>("/api/admin/marketing/journeys");
      const list: unknown[] = Array.isArray(raw)
        ? raw
        : ((raw as { journeys?: unknown[]; items?: unknown[] })?.journeys ??
          (raw as { items?: unknown[] })?.items ??
          []);
      return list
        .filter((j): j is Record<string, unknown> => typeof j === "object" && j !== null)
        .filter((j) => typeof j.code === "string")
        .map((j) => ({
          code: j.code as string,
          name: typeof j.name === "string" ? j.name : (j.code as string),
          isActive: j.isActive === true,
        }));
    },
    staleTime: 60_000,
  });

  if (countries.length === 0) {
    return (
      <Card>
        <CardContent className="text-muted-foreground p-6 text-center text-sm">
          <Trans>No active countries.</Trans>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <label htmlFor="rules-country" className="text-muted-foreground mb-1 block text-xs">
            <Trans>Country</Trans>
          </label>
          <select
            id="rules-country"
            value={countryCode}
            onChange={(e) => setSelected(e.target.value)}
            className="border-border bg-background min-h-11 w-full rounded-md border px-3 text-sm"
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag ? `${c.flag} ` : ""}
                {c.name} ({c.code})
              </option>
            ))}
          </select>
          {!row ? (
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans>
                Nothing saved for this country yet — these are the defaults the send path applies.
              </Trans>
            </p>
          ) : null}
        </div>
        <CountryRulesForm
          key={`${dbTarget}:${countryCode}`}
          countryCode={countryCode}
          row={row}
          journeys={journeysQuery.data ?? null}
          journeysLoading={journeysQuery.isPending}
          onSave={onSave}
        />
      </CardContent>
    </Card>
  );
}

function listToText(values: string[] | null | undefined): string {
  return (values ?? []).join("\n");
}

function textToList(text: string, normalize: (value: string) => string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[\n,;]+/)) {
    const value = normalize(part.trim());
    if (value && !out.includes(value)) out.push(value);
  }
  return out;
}

const normalizeDomain = (value: string) => value.toLowerCase().replace(/^@+/, "");
const normalizePhone = (value: string) => value.replace(/[\s().-]/g, "");

function CountryRulesForm({
  countryCode,
  row,
  journeys,
  journeysLoading,
  onSave,
}: {
  countryCode: string;
  row: MarketingSettings | undefined;
  journeys: JourneyOption[] | null;
  journeysLoading: boolean;
  onSave: SaveCountry;
}) {
  const { t } = useLingui();
  const initial = row ? toFormRow(row) : defaultRow(countryCode);

  const [fromName, setFromName] = useState(initial.emailFromName);
  const [fromAddress, setFromAddress] = useState(initial.emailFromAddress);
  const [maxEmails, setMaxEmails] = useState(String(initial.maxEmailsPerWeek));
  const [maxSms, setMaxSms] = useState(String(initial.maxSmsPerWeek));
  const [domains, setDomains] = useState(listToText(initial.blacklistDomains));
  const [phones, setPhones] = useState(listToText(initial.blacklistPhones));
  const [journeyCodes, setJourneyCodes] = useState<string[]>(initial.activeJourneyCodes);
  const [doubleOptIn, setDoubleOptIn] = useState(initial.doubleOptInRequired === true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  const addressOk = SENDER_ADDRESS_RE.test(fromAddress.trim());
  const nameOk = fromName.trim().length > 0 && fromName.trim().length <= SENDER_NAME_MAX;
  const emailsNum = Number.parseInt(maxEmails, 10);
  const smsNum = Number.parseInt(maxSms, 10);
  const emailsOk =
    /^\d+$/.test(maxEmails.trim()) && emailsNum >= 0 && emailsNum <= MAX_EMAILS_PER_WEEK;
  const smsOk = /^\d+$/.test(maxSms.trim()) && smsNum >= 0 && smsNum <= MAX_SMS_PER_WEEK;
  const valid = addressOk && nameOk && emailsOk && smsOk;

  // Codes saved on the row that no journey carries any more stay visible, so
  // they can be unticked instead of lingering invisibly in the filter.
  const journeyOptions = useMemo<JourneyOption[]>(() => {
    const list = journeys ?? [];
    const known = new Set(list.map((j) => j.code));
    const orphans = journeyCodes
      .filter((code) => !known.has(code))
      .map((code) => ({ code, name: code, isActive: false }));
    return [...list, ...orphans];
  }, [journeys, journeyCodes]);

  const toggleJourney = (code: string, on: boolean) => {
    setTouched(true);
    setJourneyCodes((prev) => (on ? [...prev, code] : prev.filter((c) => c !== code)));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (!valid || saving) return;
    setSaving(true);
    const ok = await onSave(
      countryCode,
      {
        emailFromName: fromName.trim(),
        emailFromAddress: fromAddress.trim().toLowerCase(),
        maxEmailsPerWeek: emailsNum,
        maxSmsPerWeek: smsNum,
        doubleOptInRequired: doubleOptIn,
        blacklistDomains: textToList(domains, normalizeDomain),
        blacklistPhones: textToList(phones, normalizePhone),
        activeJourneyCodes: journeyCodes,
      },
      {
        saving: t`Saving sending rules for ${countryCode}…`,
        saved: t`Sending rules saved for ${countryCode}`,
        error: t`Could not save the sending rules for ${countryCode}`,
      },
    );
    setSaving(false);
    if (ok) setTouched(false);
  };

  /** Any edit arms the inline validation. */
  const edit = (setter: (value: string) => void) => (value: string) => {
    setTouched(true);
    setter(value);
  };

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      {/* Sender identity */}
      <fieldset className="space-y-3">
        <legend className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <Mail className="text-muted-foreground size-4" aria-hidden />
          <Trans>Sender identity</Trans>
        </legend>
        <EnforcedNote>
          <Trans>
            Every marketing email to a lead in this country is sent from this name and address. Only
            @{SENDER_DOMAIN} addresses are accepted — the domain authenticated in SendGrid; any
            other address would be replaced by the default sender.
          </Trans>
        </EnforcedNote>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="rules-from-name" className="text-muted-foreground mb-1 block text-xs">
              <Trans>From name</Trans>
            </label>
            <Input
              id="rules-from-name"
              value={fromName}
              onChange={(e) => edit(setFromName)(e.target.value)}
              aria-invalid={touched && !nameOk ? true : undefined}
              className="min-h-11"
            />
            {touched && !nameOk ? (
              <p className="text-destructive-strong mt-1 text-xs">
                <Trans>Required, up to {SENDER_NAME_MAX} characters.</Trans>
              </p>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="rules-from-address"
              className="text-muted-foreground mb-1 block text-xs"
            >
              <Trans>From address</Trans>
            </label>
            <Input
              id="rules-from-address"
              type="email"
              value={fromAddress}
              onChange={(e) => edit(setFromAddress)(e.target.value)}
              placeholder={`info@${SENDER_DOMAIN}`}
              aria-invalid={touched && !addressOk ? true : undefined}
              className="min-h-11"
            />
            {touched && !addressOk ? (
              <p className="text-destructive-strong mt-1 text-xs">
                <Trans>Must be an address @{SENDER_DOMAIN}.</Trans>
              </p>
            ) : null}
          </div>
        </div>
      </fieldset>

      {/* Weekly caps */}
      <fieldset className="space-y-3">
        <legend className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <Gauge className="text-muted-foreground size-4" aria-hidden />
          <Trans>Weekly caps per lead</Trans>
        </legend>
        <EnforcedNote>
          <Trans>
            Counted per lead over the last 7 days, marketing messages only — service notices such as
            a trial about to end are never capped. Over the cap, the message is skipped. 0 blocks
            the channel family entirely.
          </Trans>
        </EnforcedNote>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="rules-max-emails" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Emails per week</Trans>
            </label>
            <Input
              id="rules-max-emails"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_EMAILS_PER_WEEK}
              value={maxEmails}
              onChange={(e) => edit(setMaxEmails)(e.target.value)}
              aria-invalid={touched && !emailsOk ? true : undefined}
              className="min-h-11"
            />
            {touched && !emailsOk ? (
              <p className="text-destructive-strong mt-1 text-xs">
                <Trans>A whole number from 0 to {MAX_EMAILS_PER_WEEK}.</Trans>
              </p>
            ) : null}
          </div>
          <div>
            <label htmlFor="rules-max-sms" className="text-muted-foreground mb-1 block text-xs">
              <Trans>SMS, Viber and WhatsApp per week</Trans>
            </label>
            <Input
              id="rules-max-sms"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_SMS_PER_WEEK}
              value={maxSms}
              onChange={(e) => edit(setMaxSms)(e.target.value)}
              aria-invalid={touched && !smsOk ? true : undefined}
              className="min-h-11"
            />
            {touched && !smsOk ? (
              <p className="text-destructive-strong mt-1 text-xs">
                <Trans>A whole number from 0 to {MAX_SMS_PER_WEEK}.</Trans>
              </p>
            ) : null}
          </div>
        </div>
      </fieldset>

      {/* Double opt-in */}
      <fieldset className="space-y-3">
        <legend className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <MailCheck className="text-muted-foreground size-4" aria-hidden />
          <Trans>Double opt-in</Trans>
        </legend>
        <EnforcedNote>
          <Trans>
            Only consent collected through a form needs the confirmation click; consent given in
            account settings never does.
          </Trans>
        </EnforcedNote>
        <div className="border-border flex items-start gap-3 rounded-lg border p-3">
          <div className="flex min-h-11 items-center">
            <Switch
              id="rules-double-opt-in"
              checked={doubleOptIn}
              onCheckedChange={(checked) => {
                setTouched(true);
                setDoubleOptIn(checked);
              }}
              aria-describedby="rules-double-opt-in-hint"
              className="after:-inset-y-3.5"
            />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="rules-double-opt-in" className="text-foreground text-sm font-medium">
              <Trans>Require double opt-in (email)</Trans>
            </label>
            <p id="rules-double-opt-in-hint" className="text-muted-foreground mt-0.5 text-xs">
              {doubleOptIn ? (
                <Trans>
                  On: a lead from a form gets marketing email only after clicking the confirmation
                  link; until then it is skipped as “No marketing consent”.
                </Trans>
              ) : (
                <Trans>Off: consent from a form counts as soon as it is given.</Trans>
              )}
            </p>
          </div>
        </div>
      </fieldset>

      {/* Blacklists */}
      <fieldset className="space-y-3">
        <legend className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <Ban className="text-muted-foreground size-4" aria-hidden />
          <Trans>Blacklists</Trans>
        </legend>
        <EnforcedNote>
          <Trans>
            A lead whose email is on one of these domains (or a subdomain of one), or whose phone is
            one of these numbers, is skipped for this country. One entry per line.
          </Trans>
        </EnforcedNote>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="rules-domains" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Blocked email domains</Trans>
            </label>
            <Textarea
              id="rules-domains"
              value={domains}
              onChange={(e) => edit(setDomains)(e.target.value)}
              rows={4}
              placeholder={t`example.com`}
              className="font-mono text-sm"
            />
          </div>
          <div>
            <label htmlFor="rules-phones" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Blocked phone numbers</Trans>
            </label>
            <Textarea
              id="rules-phones"
              value={phones}
              onChange={(e) => edit(setPhones)(e.target.value)}
              rows={4}
              placeholder="+40700000000"
              className="font-mono text-sm"
            />
          </div>
        </div>
      </fieldset>

      {/* Active journeys */}
      <fieldset className="space-y-3">
        <legend className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <RouteIcon className="text-muted-foreground size-4" aria-hidden />
          <Trans>Active journeys</Trans>
        </legend>
        <EnforcedNote>
          <Trans>
            Only the ticked journeys enrol leads from this country. Nothing ticked = every active
            journey applies.
          </Trans>
        </EnforcedNote>
        {journeysLoading ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Skeleton className="h-11 rounded-lg" />
            <Skeleton className="h-11 rounded-lg" />
          </div>
        ) : journeyOptions.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            <Trans>No journeys exist yet.</Trans>
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {journeyOptions.map((j) => {
              const checked = journeyCodes.includes(j.code);
              return (
                <label
                  key={j.code}
                  className="border-border has-[:checked]:border-primary has-[:checked]:bg-primary-soft flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleJourney(j.code, e.target.checked)}
                    className="accent-primary size-4 shrink-0"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="text-foreground block truncate text-sm">{j.name}</span>
                    <span className="text-muted-foreground block truncate font-mono text-xs">
                      {j.code}
                    </span>
                  </span>
                  {j.isActive ? (
                    <Badge variant="success">
                      <Trans>Active</Trans>
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      <Trans>Inactive</Trans>
                    </Badge>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </fieldset>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        {touched && !valid ? (
          <p className="text-destructive-strong text-xs sm:mr-auto">
            <Trans>Fix the highlighted fields to save.</Trans>
          </p>
        ) : null}
        <Button type="submit" className="min-h-11" disabled={saving || (touched && !valid)}>
          {saving ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Save className="size-4" aria-hidden />
          )}
          <Trans>Save rules for {countryCode}</Trans>
        </Button>
      </div>
    </form>
  );
}
